import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Camera, AudioLines, Settings, Users, Monitor, Award } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { tr } from '../locales/tr';
import { en } from '../locales/en';
import artoPhoto from '../assets/team/arto.webp';
import arenPhoto from '../assets/team/aren.webp';
import larissaPhoto from '../assets/team/larissa.webp';

const About: React.FC = () => {
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.about : en.about;

  const stats = [
    { value: "15+", label: language === 'TR' ? "Yıllık Sektör Deneyimi" : "Years of Experience" },
    { value: "80+", label: language === 'TR' ? "Memnun Marka & İş Ortağı" : "Brand Partners" },
    { value: "400+", label: language === 'TR' ? "Etkinlik Çekimi & Canlı Yayın" : "Events & Broadcasts" },
    { value: "50+", label: language === 'TR' ? "Kurumsal ve Fabrika Filmi" : "Corporate Films" },
    { value: "50+", label: language === 'TR' ? "Müzik Videosu & Konser" : "Concerts & Music Videos" }
  ];

  const studioSpecs = [
    {
      title: language === 'TR' ? "Şişli Stüdyosu" : "Istanbul Studio",
      desc: language === 'TR'
        ? "Şişli'de yer alan stüdyomuz, yaratıcı fikirlerin hızlıca görsele dönüştüğü üretim merkezimizdir."
        : "Our central Istanbul studio serves as the hub where creative visions transform into cinema-grade media.",
      icon: <Monitor className="w-5 h-5 text-white" />
    },
    {
      title: language === 'TR' ? "35 m² Çekim Alanı" : "35 m² Sound Stage",
      desc: language === 'TR'
        ? "Butik çekimler, röportajlar, dijital programlar ve ürün çekimleri için tasarlanmış izole akustik alan."
        : "Acoustically treated studio optimized for boutique productions, interviews, product shoots, and digital shows.",
      icon: <Settings className="w-5 h-5 text-white" />
    },
    {
      title: language === 'TR' ? "Çok Kameralı Reji" : "Multi-Camera Vision Switching",
      desc: language === 'TR'
        ? "Anlık canlı geçişler, çok kameralı reji masası altyapısı ve kesintisiz dijital platform akışları."
        : "Low-latency broadcast control room, multi-camera switching, and multi-platform simulcast capabilities.",
      icon: <Camera className="w-5 h-5 text-white" />
    },
    {
      title: language === 'TR' ? "32-Bit Float Ses Kaydı" : "32-Bit Float Audio Capture",
      desc: language === 'TR'
        ? "Geniş dinamik ses aralıklarını patlama veya bozulma olmadan en temiz şekilde kaydeden üst seviye ses altyapısı."
        : "Studio microphones and 32-bit float multi-track recorders delivering zero-clipping sound fidelity.",
      icon: <AudioLines className="w-5 h-5 text-white" />
    }
  ];

  return (
    <div className="pt-28 pb-16 bg-background">
      <Helmet>
        <title>{language === 'TR' ? "Hakkımızda - WE24 Agency" : "About Us - WE24 Agency"}</title>
        <meta
          name="description"
          content={
            language === 'TR'
              ? "WE24 Agency'nin kuruluşu, Şişli stüdyosu, çekirdek ekibimiz ve kurumsal uzman serbest ağımız hakkında bilgiler."
              : "Discover WE24 Agency: our Istanbul studio, leadership team, and multidisciplinary production collective."
          }
        />
        <link rel="canonical" href="https://www.we24agency.com/hakkimizda" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6">
        {/* 1. Editorial Hero */}
        <div className="max-w-3xl mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-[#d5503b] font-semibold mb-2 block">
            {language === 'TR' ? "Hakkımızda" : "About Us"}
          </span>
          <h1 className="text-4xl sm:text-6xl font-display tracking-tight text-white leading-none mb-6">
            {t.title}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* 2. Agency Story */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 mb-16 items-center">
          <div className="lg:col-span-7 flex flex-col gap-5 text-xs sm:text-sm text-muted-foreground leading-relaxed">
            <h2 className="text-2xl sm:text-3xl font-display text-white">{t.storyTitle}</h2>
            <p>{t.storyP1}</p>
            <p>{t.storyP2}</p>
          </div>
          <div className="lg:col-span-5 bg-card/25 border border-border/20 p-6 sm:p-8 rounded-2xl flex flex-col gap-4">
            <Award className="w-7 h-7 text-[#d5503b]" />
            <h3 className="text-base sm:text-lg font-bold text-white leading-tight">{t.valuesTitle}</h3>
            <ul className="flex flex-col gap-2.5 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#d5503b] mt-1.5 shrink-0" />
                <span>
                  <strong>{language === 'TR' ? "Özelleştirilmiş Çözümler: " : "Custom Tailored Solutions: "}</strong>
                  {language === 'TR'
                    ? "Her markanın ihtiyacına özel butik ekipler ve iş akışları kuruyoruz."
                    : "We build bespoke production workflows and crews for every unique brand challenge."}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#d5503b] mt-1.5 shrink-0" />
                <span>
                  <strong>{language === 'TR' ? "Kalite ve Güven: " : "Quality & Reliability: "}</strong>
                  {language === 'TR'
                    ? "Süreçlerimizin her aşamasında teknik mükemmelliği ve şeffaflığı garanti ediyoruz."
                    : "We guarantee technical excellence, on-time delivery, and full transparency."}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#d5503b] mt-1.5 shrink-0" />
                <span>
                  <strong>{language === 'TR' ? "Entegre Tasarım Dili: " : "Cohesive Design Language: "}</strong>
                  {language === 'TR'
                    ? "Video, tasarım ve yazılım disiplinlerinin birbiriyle tutarlı konuşmasını sağlıyoruz."
                    : "We ensure video, brand design, and web technology speak the exact same visual language."}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* 3. Team grid */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.25em] text-[#d5503b] font-semibold mb-1.5 block">
              {language === 'TR' ? "Çekirdek Kadro" : "Leadership & Creative"}
            </span>
            <h2 className="text-3xl sm:text-4xl font-display tracking-tight text-white">{t.teamTitle}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Larissa Davulciyan */}
            <div className="bg-card/20 border border-border/20 p-7 rounded-2xl flex flex-col items-center text-center gap-4 hover:border-[#d5503b]/40 transition-colors group">
              <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden border-2 border-border/40 group-hover:border-[#d5503b]/70 transition-all duration-300 shadow-xl bg-card/40 relative">
                <img src={larissaPhoto} alt="Larissa Davulciyan" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="flex flex-col items-center">
                <h3 className="text-lg font-bold text-white">Larissa Davulciyan</h3>
                <span className="text-xs text-[#d5503b] font-semibold tracking-wide block mt-1">
                  {language === 'TR' ? "Müşteri İlişkileri ve Proje Yönetimi" : "Client Relations & Project Management"}
                </span>
                <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-white/75 bg-white/[0.06] border border-white/10 px-2.5 py-0.5 rounded-full mt-2">
                  {language === 'TR' ? "Kurucu Ortak" : "Co-Founder"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {language === 'TR'
                  ? "Müşteri ilişkileri, iş akışları, sosyal medya planlaması ve içeriklerin onay/yayın süreçlerini koordine eder."
                  : "Coordinates client communications, strategic workflow execution, social planning, and content approvals."}
              </p>
            </div>

            {/* Harutyun Arto Davulciyan */}
            <div className="bg-card/20 border border-border/20 p-7 rounded-2xl flex flex-col items-center text-center gap-4 hover:border-[#d5503b]/40 transition-colors group">
              <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden border-2 border-border/40 group-hover:border-[#d5503b]/70 transition-all duration-300 shadow-xl bg-card/40 relative">
                <img src={artoPhoto} alt="Harutyun Arto Davulciyan" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="flex flex-col items-center">
                <h3 className="text-lg font-bold text-white">Harutyun Arto Davulciyan</h3>
                <span className="text-xs text-[#d5503b] font-semibold tracking-wide block mt-1">
                  {language === 'TR' ? "Yönetmen & Görüntü Yönetmeni" : "Director & Cinematographer"}
                </span>
                <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-white/75 bg-white/[0.06] border border-white/10 px-2.5 py-0.5 rounded-full mt-2">
                  {language === 'TR' ? "Kurucu Ortak" : "Co-Founder"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {language === 'TR'
                  ? "Sinematik filmlerin vizyonunu, kamera/ışık set yönetimini, canlı yayın reji tekniklerini ve post-prodüksiyon kurgu adımlarını yönetir."
                  : "Leads cinematic direction, camera & lighting operations, live broadcasting switchers, and post-production mastering."}
              </p>
            </div>

            {/* Aren Arda Kaya */}
            <div className="bg-card/20 border border-border/20 p-7 rounded-2xl flex flex-col items-center text-center gap-4 hover:border-[#d5503b]/40 transition-colors group">
              <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden border-2 border-border/40 group-hover:border-[#d5503b]/70 transition-all duration-300 shadow-xl bg-card/40 relative">
                <img src={arenPhoto} alt="Aren Arda Kaya" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="flex flex-col items-center">
                <h3 className="text-lg font-bold text-white">Aren Arda Kaya</h3>
                <span className="text-xs text-[#d5503b] font-semibold tracking-wide block mt-1">
                  {language === 'TR' ? "Art Direktör" : "Art Director"}
                </span>
                <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-white/75 bg-white/[0.06] border border-white/10 px-2.5 py-0.5 rounded-full mt-2">
                  {language === 'TR' ? "Kurucu Ortak" : "Co-Founder"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {language === 'TR'
                  ? "Marka kimliği tasarımlarını, kreatif sosyal medya şablonlarını, arayüz konseptlerini ve görsel standartları tasarlar."
                  : "Designs corporate identity systems, creative digital content, UI concepts, and overall visual standards."}
              </p>
            </div>
          </div>

          <div className="mt-6 bg-card/10 border border-border/20 p-5 rounded-2xl text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold text-white flex items-center justify-center gap-2">
              <Users className="w-4 h-4 text-[#d5503b]" />
              {language === 'TR' ? "Esnek ve Geniş Üretim Ağı" : "Extended Professional Production Collective"}
            </span>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              {language === 'TR'
                ? "Büyük ölçekli setlerde, film prodüksiyonlarında ve yazılım projelerinde stüdyomuz bünyesinde çalışan kurumsal serbest çalışan (freelance) görüntü yönetmenleri, asistanlar, ses mühendisleri ve yazılım geliştiricilerle genişleyen entegre bir yapıda faaliyet gösteriyoruz."
                : "For large-scale cinema sets, multi-location film productions, and complex software projects, we deploy an integrated network of veteran freelance cinematographers, sound engineers, and developers."}
            </p>
          </div>
        </div>

        {/* 4. Studio & Technical Infrastructure */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.25em] text-[#d5503b] font-semibold mb-1.5 block">
              {language === 'TR' ? "Stüdyo" : "Studio"}
            </span>
            <h2 className="text-3xl sm:text-4xl font-display tracking-tight text-white">{t.studioTitle}</h2>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mt-1.5">
              {t.studioSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {studioSpecs.map((spec) => (
              <div
                key={spec.title}
                className="bg-card/25 border border-border/20 p-6 rounded-2xl flex flex-col gap-3 hover:border-white/20 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-border/20 border border-border/30 flex items-center justify-center">
                  {spec.icon}
                </div>
                <h3 className="font-bold text-white text-sm sm:text-base leading-tight">{spec.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{spec.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 5. Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center bg-card/10 border border-border/20 p-8 sm:p-10 rounded-3xl">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col gap-1">
              <span className="text-3xl sm:text-4xl font-display font-bold text-white">{s.value}</span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center max-w-xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-display text-white mb-4">
            {language === 'TR' ? "Bizimle İletişime Geçin" : "Get in Touch With Us"}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
            {language === 'TR'
              ? "Projelerinizi hayata geçirmek, stüdyomuzu ziyaret etmek veya teklif almak için bizimle hemen iletişime geçebilirsiniz."
              : "Let's bring your vision to life. Contact our studio to discuss your project requirements or request a proposal."}
          </p>
          <Link
            to="/iletisim"
            className="px-7 py-3 bg-white text-black hover:bg-white/90 text-xs sm:text-sm font-semibold rounded-full inline-block shadow-lg"
          >
            {language === 'TR' ? "İletişime Geç" : "Contact Studio"}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
